import type { PricingPageCardConfig } from "./PricingPage";

// Message descriptors — IDs match messages.PricingCards in web-app.
// defaultMessage is the English fallback used by PRICING_CONFIGS (identity translate).
const m = {
  Category: {
    Projects: { id: "pricingCards.category.projects", defaultMessage: "Projects" },
    Storage: { id: "pricingCards.category.storage", defaultMessage: "Storage" },
    Import: { id: "pricingCards.category.import", defaultMessage: "Import" },
    Export: { id: "pricingCards.category.export", defaultMessage: "Export" },
    GenerativeTools: {
      id: "pricingCards.category.generativeTools",
      defaultMessage: "Generative Tools",
    },
    CollaborationDocumentation: {
      id: "pricingCards.category.collaborationDocumentation",
      defaultMessage: "Collaboration & Documentation",
    },
    SupportTraining: {
      id: "pricingCards.category.supportTraining",
      defaultMessage: "Support & Training",
    },
    SecurityCompliance: {
      id: "pricingCards.category.securityCompliance",
      defaultMessage: "Security & Compliance",
    },
  },
  Seats: {
    TwoContributor: {
      BeforeMark: { id: "pricingCards.seats.twoContributor.beforeMark", defaultMessage: "2 " },
      Mark: { id: "pricingCards.seats.twoContributor.mark", defaultMessage: "Contributor" },
      AfterMark: { id: "pricingCards.seats.twoContributor.afterMark", defaultMessage: " seats" },
    },
    UpToTenEditor: {
      BeforeMark: {
        id: "pricingCards.seats.upToTenEditor.beforeMark",
        defaultMessage: "Up to 10 ",
      },
      Mark: { id: "pricingCards.seats.upToTenEditor.mark", defaultMessage: "Editor" },
      AfterMark: { id: "pricingCards.seats.upToTenEditor.afterMark", defaultMessage: " seats" },
    },
    UnlimitedEditor: {
      BeforeMark: {
        id: "pricingCards.seats.unlimitedEditor.beforeMark",
        defaultMessage: "Unlimited ",
      },
      Mark: { id: "pricingCards.seats.unlimitedEditor.mark", defaultMessage: "Editor" },
      AfterMark: { id: "pricingCards.seats.unlimitedEditor.afterMark", defaultMessage: " seats" },
    },
    SeatManager: {
      Line: { id: "pricingCards.seats.seatManager", defaultMessage: "Seat manager" },
    },
  },
  Free: {
    PlanName: { id: "pricingCards.free.planName", defaultMessage: "Free" },
    Feature: {
      UpTo2Projects: {
        id: "pricingCards.free.feature.upTo2Projects",
        defaultMessage: "2 projects",
      },
      Platforms: {
        id: "pricingCards.free.feature.platforms",
        defaultMessage: "macOS, iPadOS, Windows and visionOS",
      },
      CloudStorage: {
        id: "pricingCards.free.feature.cloudStorage",
        defaultMessage: "Cloud storage",
      },
      ImportFormats: {
        id: "pricingCards.free.feature.importFormats",
        defaultMessage: "XT, STEP, IGES, DWG, STL",
      },
      ImportSolidworks: {
        id: "pricingCards.free.feature.importSolidworks",
        defaultMessage: "SOLIDWORKS",
      },
      ImportImages: { id: "pricingCards.free.feature.importImages", defaultMessage: "Images" },
      ExportFormats: {
        id: "pricingCards.free.feature.exportFormats",
        defaultMessage: "Basic quality 3MF, STL",
      },
      GenerativeRenders: {
        id: "pricingCards.free.feature.generativeRenders",
        defaultMessage: "20 generations/month",
      },
      Support: {
        id: "pricingCards.free.feature.support",
        defaultMessage: "Basic technical support",
      },
    },
  },
  Individual: {
    PlanName: { id: "pricingCards.individual.planName", defaultMessage: "Solo" },
    Tagline: { id: "pricingCards.individual.tagline", defaultMessage: "Everything in Free plus" },
    Feature: {
      UpTo100Projects: {
        id: "pricingCards.individual.feature.upTo100Projects",
        defaultMessage: "100 projects",
      },
      VersionHistory: {
        id: "pricingCards.individual.feature.versionHistory",
        defaultMessage: "Version retention up to 1 year",
      },
      CentralizedCloudStorage: {
        id: "pricingCards.individual.feature.centralizedCloudStorage",
        defaultMessage: "Centralized cloud storage",
      },
      ExportShapr3D: {
        id: "pricingCards.individual.feature.exportShapr3D",
        defaultMessage: "Shapr3D",
      },
      ExportHighQualityMeshes: {
        id: "pricingCards.individual.feature.exportHighQualityMeshes",
        defaultMessage: "High quality 3MF, STL",
      },
      ExportStepIges: {
        id: "pricingCards.individual.feature.exportStepIges",
        defaultMessage: "STEP, IGES",
      },
      ExportDxfGroup: {
        id: "pricingCards.individual.feature.exportDxfGroup",
        defaultMessage: "DXF, DWG, SVG, PDF",
      },
      ExportUsdGroup: {
        id: "pricingCards.individual.feature.exportUsdGroup",
        defaultMessage: "USDZ, GLB, OBJ",
      },
      GenerativeRenders: {
        id: "pricingCards.individual.feature.generativeRenders",
        defaultMessage: "100 generations/month",
      },
      TechnicalDrawings: {
        id: "pricingCards.individual.feature.technicalDrawings",
        defaultMessage: "Technical drawings for straightforward manufacturing hand-offs",
      },
      RealtimeRenders: {
        id: "pricingCards.individual.feature.realtimeRenders",
        defaultMessage: "Real-time renders with 100+ materials",
      },
      AR: {
        id: "pricingCards.individual.feature.ar",
        defaultMessage: "Integrated AR on iPad for instant digital prototyping",
      },
      ShareLinks: {
        id: "pricingCards.individual.feature.shareLinks",
        defaultMessage: "Secure links for sharing 3D models, AR preview, and technical drawings",
      },
      Support: {
        id: "pricingCards.individual.feature.support",
        defaultMessage: "Priority technical support",
      },
    },
  },
  Studio: {
    PlanName: { id: "pricingCards.studio.planName", defaultMessage: "Studio" },
    Tagline: { id: "pricingCards.studio.tagline", defaultMessage: "Everything in Solo plus" },
    PriceSubline: { id: "pricingCards.studio.priceSubline", defaultMessage: "per Editor" },
    Feature: {
      UnlimitedProjects: {
        id: "pricingCards.studio.feature.unlimitedProjects",
        defaultMessage: "Unlimited projects",
      },
      UnlimitedTeamspaces: {
        id: "pricingCards.studio.feature.unlimitedTeamspaces",
        defaultMessage: "Unlimited teamspaces",
      },
      VersionHistory: {
        id: "pricingCards.studio.feature.versionHistory",
        defaultMessage: "Version retention up to 3 years",
      },
      DataOwnership: {
        id: "pricingCards.studio.feature.dataOwnership",
        defaultMessage: "Company ownership of all data",
      },
      CentralizedCloudStorage: {
        id: "pricingCards.studio.feature.centralizedCloudStorage",
        defaultMessage: "Centralized cloud storage",
      },
      ImportUsdcJt: { id: "pricingCards.studio.feature.importUsdcJt", defaultMessage: "USDC, JT" },
      ImportCadFormats: {
        id: "pricingCards.studio.feature.importCadFormats",
        defaultMessage: "Catia (V5), NX, Creo, Solid Edge",
      },
      ImportImages: { id: "pricingCards.studio.feature.importImages", defaultMessage: "Images" },
      ExportJt: { id: "pricingCards.studio.feature.exportJt", defaultMessage: "JT" },
      ExportXt: { id: "pricingCards.studio.feature.exportXt", defaultMessage: "X_T, X_B" },
      GenerativeRenders: {
        id: "pricingCards.studio.feature.generativeRenders",
        defaultMessage: "1000 generations/month",
      },
      MaterialLibraries: {
        id: "pricingCards.studio.feature.materialLibraries",
        defaultMessage: "Custom material libraries for consistent brand presentations",
      },
      VisionPro: {
        id: "pricingCards.studio.feature.visionPro",
        defaultMessage: "Vision Pro for individual, immersive design sessions",
      },
      Support: {
        id: "pricingCards.studio.feature.support",
        defaultMessage: "Priority technical support",
      },
    },
  },
  Enterprise: {
    PlanName: { id: "pricingCards.enterprise.planName", defaultMessage: "Enterprise" },
    Tagline: { id: "pricingCards.enterprise.tagline", defaultMessage: "Everything in Studio plus" },
    Feature: {
      UnlimitedProjects: {
        id: "pricingCards.enterprise.feature.unlimitedProjects",
        defaultMessage: "Unlimited projects",
      },
      UnlimitedTeamsTeamspaces: {
        id: "pricingCards.enterprise.feature.unlimitedTeamsTeamspaces",
        defaultMessage: "Unlimited teams and teamspaces",
      },
      VersionHistory: {
        id: "pricingCards.enterprise.feature.versionHistory",
        defaultMessage: "Unlimited version retention",
      },
      DataOwnership: {
        id: "pricingCards.enterprise.feature.dataOwnership",
        defaultMessage: "Company ownership of all data",
      },
      DataStorage: {
        id: "pricingCards.enterprise.feature.dataStorage",
        defaultMessage: "Flexible data storage options:",
      },
      DataStorageSingleTenant: {
        id: "pricingCards.enterprise.feature.dataStorage.singleTenant",
        defaultMessage: "Single tenant",
      },
      DataStoragePrivateCloud: {
        id: "pricingCards.enterprise.feature.dataStorage.privateCloud",
        defaultMessage: "Private cloud",
      },
      DataStorageOnPremise: {
        id: "pricingCards.enterprise.feature.dataStorage.onPremise",
        defaultMessage: "On-premises",
      },
      DataStorageCloudOff: {
        id: "pricingCards.enterprise.feature.dataStorage.cloudOff",
        defaultMessage: "Cloud off",
      },
      ImportCustom: {
        id: "pricingCards.enterprise.feature.importCustom",
        defaultMessage: "Custom import options",
      },
      ExportCustom: {
        id: "pricingCards.enterprise.feature.exportCustom",
        defaultMessage: "Custom export options",
      },
      GenerativeRenders: {
        id: "pricingCards.enterprise.feature.generativeRenders",
        defaultMessage: "Unlimited generations",
      },
      VisionPro: {
        id: "pricingCards.enterprise.feature.visionPro",
        defaultMessage: "Vision Pro for immersive design reviews in XR\u2013even remotely",
      },
      Onboarding: {
        id: "pricingCards.enterprise.feature.onboarding",
        defaultMessage: "Accelerated onboarding & dedicated account manager",
      },
      SecurityCompliance: {
        id: "pricingCards.enterprise.feature.securityCompliance",
        defaultMessage: "ISO 27001 & SOC 2",
      },
    },
  },
} as const;

export type PricingPlan = "free" | "individual" | "studio" | "enterprise";

type PricingCardConfig = Omit<PricingPageCardConfig, "ctaSlot" | "children">;

// Structurally compatible with react-intl's FormatMessage / useIntl().formatMessage.
type Descriptor = { id: string; defaultMessage: string };
type FormatMessage = (descriptor: Descriptor) => string;

const identity: FormatMessage = (d) => d.defaultMessage;

/**
 * Creates pricing configs with all user-visible strings translated via `t`.
 *
 * Web-app:  pass `useIntl().formatMessage` — IDs match messages.PricingCards.
 * Webflow:  use the pre-built `PRICING_CONFIGS` export (identity = English defaults).
 */
export function createPricingConfigs(
  t: FormatMessage = identity
): Record<PricingPlan, PricingCardConfig> {
  return {
    free: {
      planName: t(m.Free.PlanName),
      planBadgeVariant: "default",
      price: "$0",
      pricePeriod: "/month",
      features: [
        {
          category: t(m.Category.Projects),
          sectionKey: "projects" as const,
          items: [{ text: t(m.Free.Feature.UpTo2Projects) }, { text: t(m.Free.Feature.Platforms) }],
        },
        {
          category: t(m.Category.Storage),
          sectionKey: "storage" as const,
          items: [{ text: t(m.Free.Feature.CloudStorage) }],
        },
        {
          category: t(m.Category.Import),
          sectionKey: "import" as const,
          items: [
            { text: t(m.Free.Feature.ImportFormats) },
            { text: t(m.Free.Feature.ImportSolidworks) },
            { text: t(m.Free.Feature.ImportImages) },
          ],
        },
        {
          category: t(m.Category.Export),
          sectionKey: "export" as const,
          items: [{ text: t(m.Free.Feature.ExportFormats) }],
        },
        {
          category: t(m.Category.GenerativeTools),
          sectionKey: "generativeTools" as const,
          items: [{ text: t(m.Free.Feature.GenerativeRenders) }],
        },
        {
          category: t(m.Category.SupportTraining),
          sectionKey: "supportTraining" as const,
          items: [{ text: t(m.Free.Feature.Support) }],
        },
      ],
    },

    individual: {
      planName: t(m.Individual.PlanName),
      planBadgeVariant: "blue",
      price: "$32.99",
      pricePeriod: "/month",
      subtitle: "$249.99 Billed annually, excluding tax",
      tagline: t(m.Individual.Tagline),
      highlighted: true,
      features: [
        {
          category: t(m.Category.Projects),
          sectionKey: "projects" as const,
          items: [
            { text: t(m.Individual.Feature.UpTo100Projects) },
            { text: t(m.Individual.Feature.VersionHistory) },
          ],
        },
        {
          category: t(m.Category.Storage),
          sectionKey: "storage" as const,
          items: [{ text: t(m.Individual.Feature.CentralizedCloudStorage) }],
        },
        {
          category: t(m.Category.Import),
          sectionKey: "import" as const,
          items: [
            { text: t(m.Free.Feature.ImportFormats) },
            { text: t(m.Free.Feature.ImportSolidworks) },
            { text: t(m.Free.Feature.ImportImages) },
          ],
        },
        {
          category: t(m.Category.Export),
          sectionKey: "export" as const,
          items: [
            { text: t(m.Individual.Feature.ExportShapr3D) },
            { text: t(m.Individual.Feature.ExportHighQualityMeshes) },
            { text: t(m.Individual.Feature.ExportStepIges) },
            { text: t(m.Individual.Feature.ExportDxfGroup) },
            { text: t(m.Individual.Feature.ExportUsdGroup) },
          ],
        },
        {
          category: t(m.Category.GenerativeTools),
          sectionKey: "generativeTools" as const,
          items: [{ text: t(m.Individual.Feature.GenerativeRenders) }],
        },
        {
          category: t(m.Category.CollaborationDocumentation),
          sectionKey: "collaborationDocumentation" as const,
          items: [
            { text: t(m.Individual.Feature.TechnicalDrawings) },
            { text: t(m.Individual.Feature.RealtimeRenders) },
            { text: t(m.Individual.Feature.AR) },
            { text: t(m.Individual.Feature.ShareLinks) },
          ],
        },
        {
          category: t(m.Category.SupportTraining),
          sectionKey: "supportTraining" as const,
          items: [{ text: t(m.Individual.Feature.Support) }],
        },
      ],
      seatTypes: [
        {
          kind: "highlighted",
          beforeMark: t(m.Seats.TwoContributor.BeforeMark),
          markText: t(m.Seats.TwoContributor.Mark),
          afterMark: t(m.Seats.TwoContributor.AfterMark),
          markVariant: "yellow",
        },
      ],
    },

    studio: {
      planName: t(m.Studio.PlanName),
      planBadgeVariant: "default",
      price: "$53.99",
      pricePeriod: "/month",
      priceSubline: t(m.Studio.PriceSubline),
      subtitle: "$449.99 Billed annually, excluding tax",
      tagline: t(m.Studio.Tagline),
      features: [
        {
          category: t(m.Category.Projects),
          sectionKey: "projects" as const,
          items: [
            { text: t(m.Studio.Feature.UnlimitedProjects) },
            { text: t(m.Studio.Feature.UnlimitedTeamspaces) },
            { text: t(m.Studio.Feature.VersionHistory) },
          ],
        },
        {
          category: t(m.Category.Storage),
          sectionKey: "storage" as const,
          items: [
            { text: t(m.Studio.Feature.DataOwnership) },
            { text: t(m.Studio.Feature.CentralizedCloudStorage) },
          ],
        },
        {
          category: t(m.Category.Import),
          sectionKey: "import" as const,
          items: [
            { text: t(m.Studio.Feature.ImportUsdcJt) },
            { text: t(m.Studio.Feature.ImportCadFormats) },
            { text: t(m.Studio.Feature.ImportImages) },
          ],
        },
        {
          category: t(m.Category.Export),
          sectionKey: "export" as const,
          items: [{ text: t(m.Studio.Feature.ExportJt) }, { text: t(m.Studio.Feature.ExportXt) }],
        },
        {
          category: t(m.Category.GenerativeTools),
          sectionKey: "generativeTools" as const,
          items: [{ text: t(m.Studio.Feature.GenerativeRenders) }],
        },
        {
          category: t(m.Category.CollaborationDocumentation),
          sectionKey: "collaborationDocumentation" as const,
          items: [
            { text: t(m.Studio.Feature.MaterialLibraries) },
            { text: t(m.Studio.Feature.VisionPro) },
          ],
        },
        {
          category: t(m.Category.SupportTraining),
          sectionKey: "supportTraining" as const,
          items: [{ text: t(m.Studio.Feature.Support) }],
        },
      ],
      seatTypes: [
        {
          kind: "highlighted",
          beforeMark: t(m.Seats.UpToTenEditor.BeforeMark),
          markText: t(m.Seats.UpToTenEditor.Mark),
          afterMark: t(m.Seats.UpToTenEditor.AfterMark),
          markVariant: "blue",
        },
        {
          kind: "highlighted",
          beforeMark: t(m.Seats.TwoContributor.BeforeMark),
          markText: t(m.Seats.TwoContributor.Mark),
          afterMark: t(m.Seats.TwoContributor.AfterMark),
          markVariant: "yellow",
        },
      ],
    },

    enterprise: {
      planName: t(m.Enterprise.PlanName),
      planBadgeVariant: "dark",
      price: "Contact Sales",
      pricePeriod: "",
      tagline: t(m.Enterprise.Tagline),
      features: [
        {
          category: t(m.Category.Projects),
          sectionKey: "projects" as const,
          items: [
            { text: t(m.Enterprise.Feature.UnlimitedProjects), icon: "dot" as const },
            { text: t(m.Enterprise.Feature.UnlimitedTeamsTeamspaces), icon: "dot" as const },
            { text: t(m.Enterprise.Feature.VersionHistory), icon: "dot" as const },
          ],
        },
        {
          category: t(m.Category.Storage),
          sectionKey: "storage" as const,
          items: [
            { text: t(m.Enterprise.Feature.DataOwnership), icon: "dot" as const },
            {
              text: t(m.Enterprise.Feature.DataStorage),
              icon: "dot" as const,
              subItems: [
                t(m.Enterprise.Feature.DataStorageSingleTenant),
                t(m.Enterprise.Feature.DataStoragePrivateCloud),
                t(m.Enterprise.Feature.DataStorageOnPremise),
                t(m.Enterprise.Feature.DataStorageCloudOff),
              ],
            },
          ],
        },
        {
          category: t(m.Category.Import),
          sectionKey: "import" as const,
          items: [{ text: t(m.Enterprise.Feature.ImportCustom), icon: "dot" as const }],
        },
        {
          category: t(m.Category.Export),
          sectionKey: "export" as const,
          items: [{ text: t(m.Enterprise.Feature.ExportCustom), icon: "dot" as const }],
        },
        {
          category: t(m.Category.GenerativeTools),
          sectionKey: "generativeTools" as const,
          items: [{ text: t(m.Enterprise.Feature.GenerativeRenders), icon: "dot" as const }],
        },
        {
          category: t(m.Category.CollaborationDocumentation),
          sectionKey: "collaborationDocumentation" as const,
          items: [{ text: t(m.Enterprise.Feature.VisionPro), icon: "dot" as const }],
        },
        {
          category: t(m.Category.SupportTraining),
          sectionKey: "supportTraining" as const,
          items: [{ text: t(m.Enterprise.Feature.Onboarding), icon: "dot" as const }],
        },
        {
          category: t(m.Category.SecurityCompliance),
          sectionKey: "securityCompliance" as const,
          items: [{ text: t(m.Enterprise.Feature.SecurityCompliance), icon: "dot" as const }],
        },
      ],
      seatTypes: [
        {
          kind: "highlighted",
          beforeMark: t(m.Seats.UnlimitedEditor.BeforeMark),
          markText: t(m.Seats.UnlimitedEditor.Mark),
          afterMark: t(m.Seats.UnlimitedEditor.AfterMark),
          markVariant: "blue",
        },
        {
          kind: "plain",
          text: t(m.Seats.SeatManager.Line),
        },
      ],
    },
  };
}

export const PRICING_CONFIGS = createPricingConfigs();
